import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Download, Loader2 } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { PlsMap } from "@/components/pls/PlsMap";
import { PlsFilters } from "@/components/pls/PlsFilters";
import { PlsTable } from "@/components/pls/PlsTable";
import { appsApi, fmtDate } from "@/lib/appsApi";
import { useNoIndex } from "@/lib/useNoIndex";
import { parsePls, filterRows, countBy, toCsv, CLASSES } from "@/lib/pls";

const Stat = ({ label, value, color, testid }) => (
  <div className="rounded-3xl border border-sky-100 bg-white p-5" data-testid={testid}>
    <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-slate-500">
      {color && <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} />}{label}
    </p>
    <p className="mt-2 font-display text-3xl font-semibold tracking-tight text-ink">{value.toLocaleString()}</p>
  </div>
);

export default function PlsExplorer() {
  useNoIndex();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [f, setF] = useState({ q: "", states: [], classes: [], ersaOnly: false });
  const [focus, setFocus] = useState(null);

  useEffect(() => {
    document.title = "PLS data explorer | Aero Soft Solutions";
    appsApi.get("/auth/me")
      .then((r) => (r?.data?.role === "admin" ? appsApi.get("/admin/shared/pls.json/download", { responseType: "text", transformResponse: (x) => x }) : Promise.reject(new Error("admin"))))
      .then((r) => {
        try {
          setData(parsePls(JSON.parse(r?.data ?? "")));
        } catch {
          setError("The current pls.json isn't valid JSON, so it can't be displayed. Replace it on the /apps page.");
        }
      })
      .catch(() => navigate("/apps"));
  }, [navigate]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const filtered = useMemo(() => filterRows(rows, f), [rows, f]);
  const classCounts = useMemo(() => countBy(rows, "aero_class"), [rows]);
  const stateCounts = useMemo(() => countBy(rows, "state"), [rows]);
  const focusOn = (a) => {
    setFocus({ ...a });
    document.querySelector("[data-testid='pls-map']")?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const exportCsv = () => {
    const url = URL.createObjectURL(new Blob([toCsv(filtered)], { type: "text/csv" }));
    const a = Object.assign(document.createElement("a"), { href: url, download: "pls-filtered.csv" });
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="sky-wash min-h-screen" data-testid="pls-explorer-page">
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-6">
        <div className="flex items-center justify-between gap-4">
          <Logo />
          <button onClick={() => navigate("/apps")} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm text-ink hover:border-sky-600" data-testid="pls-back-button"><ArrowLeft className="h-4 w-4" />Back</button>
        </div>
        <p className="mt-12 font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Plan Land &amp; Stay · pls.json</p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="font-display text-4xl font-semibold tracking-[-0.03em] text-ink sm:text-5xl">Airfield <span className="font-serif font-normal italic text-sky-600">register</span></h1>
          {data && <p className="font-mono text-xs text-slate-600" data-testid="pls-meta">{data.meta.source || "pls.json"}{data.meta.generated_at ? ` · generated ${fmtDate(data.meta.generated_at)}` : ""}</p>}
        </div>
        {!data && !error && <div className="flex justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div>}
        {error && <p className="mt-10 rounded-3xl border border-amber-200 bg-amber-50 p-6 text-amber-900" data-testid="pls-error">{error}</p>}
        {data && (
          <>
            <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6" data-testid="pls-stats">
              <Stat label="Total" value={rows.length} testid="pls-stat-total" />
              {Object.entries(CLASSES).map(([k, c]) => <Stat key={k} label={c.label} value={classCounts[k] ?? 0} color={c.color} testid={`pls-stat-${k}`} />)}
            </div>
            <div className="mt-8"><PlsFilters f={f} setF={setF} classCounts={classCounts} stateCounts={stateCounts} /></div>
            <div className="mt-6 flex items-center justify-between gap-3">
              <p className="text-sm text-slate-600" data-testid="pls-result-count">Showing <strong className="text-ink">{filtered.length.toLocaleString()}</strong> of {rows.length.toLocaleString()} airfields</p>
              <button onClick={exportCsv} disabled={!filtered.length} className="inline-flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50" data-testid="pls-export-csv"><Download className="h-4 w-4" />Export CSV</button>
            </div>
            <div className="mt-4"><PlsMap rows={filtered} focus={focus} /></div>
            <div className="mt-6"><PlsTable rows={filtered} onFocus={focusOn} /></div>
          </>
        )}
      </div>
    </div>
  );
}
